import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

/**
 * TrendChart — theme-aware multi-series area chart (light/dark via CSS variables).
 *
 * @param {Array}  data    — rows, e.g. [{ label: 'Mon', submissions: 3, locks: 2 }]
 * @param {Array}  series  — [{ key, label, color }] color = palette key ('primary'|'success'|'info'|...)
 * @param {string} [xKey='label']
 * @param {number} [height=240]
 */
const TrendChart = ({ data, series, xKey = 'label', height = 240 }) => {
  const theme = useTheme();
  const paletteColor = (name) => theme.vars?.palette?.[name]?.main || theme.palette[name]?.main;
  const textSecondary = theme.vars?.palette?.text?.secondary || theme.palette.text.secondary;
  const divider = theme.vars?.palette?.divider || theme.palette.divider;
  const paper = theme.vars?.palette?.background?.paper || theme.palette.background.paper;

  return (
    <Box sx={{ width: '100%', height, px: 1 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -18 }}>
          <defs>
            {series.map(({ key, color }) => (
              <linearGradient key={key} id={`trend-fill-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={paletteColor(color)} stopOpacity={0.25} />
                <stop offset="100%" stopColor={paletteColor(color)} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={divider} vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11, fill: textSecondary }}
            axisLine={{ stroke: divider }}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: textSecondary }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: paper,
              border: `1px solid ${divider}`,
              borderRadius: 10,
              fontSize: 12,
              boxShadow: '0 4px 12px rgba(16,24,40,0.12)',
            }}
            labelStyle={{ fontWeight: 600 }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
          {series.map(({ key, label, color }) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={label}
              stroke={paletteColor(color)}
              strokeWidth={2}
              fill={`url(#trend-fill-${key})`}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TrendChart;
